import {
  CssBaseline,
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material";
import type { ReactNode } from "react";
import './theme.d.ts';

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    // AKRBIT Brand Colors
    purple: {
      100: "#E6E3FF",
      200: "#C7C0FF",
      300: "#9F96FF",
      400: "#6C63FF",
      500: "#4A42D9",
      600: "#3832B3",
    },
    cyan: {
      100: "#E0F9FF",
      200: "#B3EFFF",
      300: "#5CE1FF",
      400: "#00D9FF",
      500: "#00A8CC",
      600: "#007A99",
    },
    accent: {
      100: "#E0FFF5",
      200: "#B3FFE8",
      300: "#5CFFBD",
      400: "#00FF9D",
      500: "#00CC7D",
      600: "#00995E",
    },
    gray: {
      100: "#F9F9F9",
      200: "#F1F1F1",
      300: "#9E9E9E",
      400: "#818181",
      500: "#5A5A5A",
    },
    primary: {
      main: "#6C63FF", // purple-400
      light: "#9F96FF", // purple-300
      dark: "#4A42D9", // purple-500
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#00D9FF", // cyan-400
      light: "#5CE1FF", // cyan-300
      dark: "#00A8CC", // cyan-500
      contrastText: "#000000",
    },
    error: {
      main: "#DC1B24",
    },
    background: {
      default: "#FFFFFF",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#000000",
      secondary: "#818181",
      disabled: "#9E9E9E",
    },
  },
  typography: {
    fontFamily: "Manrope, Helvetica, Arial, sans-serif",
    h1: {
      fontSize: "48px",
      "@media (max-width:992px)": {
        fontSize: "36px",
      },
      fontWeight: 500,
      letterSpacing: "-2%",
      lineHeight: 1.2,
    },
    h2: {
      fontSize: "60px",
      "@media (max-width:992px)": {
        fontSize: "48px !important",
      },
      fontWeight: 300,
      letterSpacing: "2%",
      lineHeight: 1.2,
    },
    h3: {
      fontSize: "72px",
      "@media (max-width:992px)": {
        fontSize: "48px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    h4: {
      fontSize: "60px",
      "@media (max-width:992px)": {
        fontSize: "28px !important",
      },
      fontWeight: 300,
      letterSpacing: "1.2px",
      lineHeight: 1.2,
    },
    h5: {
      fontSize: "40px",
      "@media (max-width:992px)": {
        fontSize: "18px !important",
      },
      fontWeight: 300,
      lineHeight: 1.2,
    },
    h6: {
      fontSize: "72px",
      "@media (max-width:1192px)": {
        fontSize: "54px",
      },
      "@media (max-width:992px)": {
        fontSize: "36px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle1: {
      fontSize: "22px",
      "@media (max-width:992px)": {
        fontSize: "16px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle2: {
      fontSize: "22px",
      "@media (max-width:992px)": {
        fontSize: "18px",
      },
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle3: {
      fontSize: "24px",
      "@media (max-width:992px)": {
        fontSize: "20px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle4: {
      fontSize: "28px",
      "@media (max-width:992px)": {
        fontSize: "20px",
      },
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle5: {
      fontSize: "28px",
      "@media (max-width:992px)": {
        fontSize: "20px",
      },
      fontWeight: 600,
      lineHeight: 1.2,
    },
    subtitle6: {
      fontSize: "30px",
      "@media (max-width:992px)": {
        fontSize: "24px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle7: {
      fontSize: "32px",
      "@media (max-width:992px)": {
        fontSize: "28px",
      },
      fontWeight: 400,
      lineHeight: 1.2,
    },
    subtitle8: {
      fontSize: "36px",
      "@media (max-width:992px)": {
        fontSize: "28px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle9: {
      fontSize: "38px",
      "@media (max-width:992px)": {
        fontSize: "30px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle10: {
      fontSize: "40px",
      "@media (max-width:992px)": {
        fontSize: "28px",
      },
      fontWeight: 400,
      lineHeight: 1.2,
    },
    subtitle11: {
      fontSize: "22px",
      "@media (max-width:992px)": {
        fontSize: "20px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle12: {
      fontSize: "28px",
      fontWeight: 500,
      lineHeight: 1.2,
      textTransform: "uppercase",
    },
    subtitle13: {
      fontSize: "36px",
      "@media (max-width:992px)": {
        fontSize: "24px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
      textTransform: "uppercase",
    },
    subtitle14: {
      fontSize: "36px",
      "@media (max-width:1192px)": {
        fontSize: "28px",
      },
      "@media (max-width:992px)": {
        fontSize: "20px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
      textTransform: "uppercase",
    },
    body1: {
      fontSize: "12px",
      fontWeight: 500,
      lineHeight: 1.2,
    },
    body2: {
      fontSize: "13px",
      "@media (max-width:992px)": {
        fontSize: "14px",
        fontWeight: 600,
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    body3: {
      fontSize: "13px",
      "@media (max-width:992px)": {
        fontSize: "14px",
        lineHeight: 1.42,
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    body4: {
      fontSize: "16px",
      "@media (max-width:992px)": {
        fontSize: "12px",
        lineHeight: 1.42,
      },
      fontWeight: 500,
      lineHeight: 1.42,
    },
    body5: {
      fontSize: "16px",
      "@media (max-width:992px)": {
        fontSize: "14px",
        lineHeight: 1.42,
      },
      fontWeight: 600,
      lineHeight: 1.2,
    },
    body6: {
      fontSize: "18px",
      "@media (max-width:992px)": {
        fontSize: "14px",
        lineHeight: 1.42,
      },
      fontWeight: 600,
      lineHeight: 1.2,
    },
    body7: {
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: 1.42,
    },
    body8: {
      fontSize: "18px",
      "@media (max-width:992px)": {
        fontSize: "16px",
      },
      fontWeight: 600,
      lineHeight: "normal",
    },
    button: {
      fontSize: "16px",
      "@media (max-width:992px)": {
        fontSize: "14px",
      },
      fontWeight: 600,
      lineHeight: "normal",
      textTransform: "none",
    },
    caption: {
      fontSize: "13px",
      fontWeight: 500,
      lineHeight: 1.2,
      fontStyle: "normal",
    },
    overline: {
      fontSize: "0.8125rem",
      fontWeight: 500,
      lineHeight: 1.2,
    },
  },
});

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};
